import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { doSwaggerCall } from '../../../../hooks/useSwaggerClient';
import {
    buildExistingCustomFields,
    convertMissingFieldsToBookingFields
} from './fhFieldUtils';

const FhItemDetailsPanel = ({ fhItem, matchedActivity, details, companyId }) => {
    const [activiterTickets, setActiviterTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [importing, setImporting] = useState({});

    // Load full activity details (with tickets) for matched activities
    const loadTickets = useCallback(() => {
        if (!matchedActivity) {
            setActiviterTickets([]);
            return;
        }
        // Only show full loading spinner on initial load, not on refreshes
        if (!initialLoadDone) {
            setLoadingTickets(true);
        }
        doSwaggerCall('activities', 'getActivityByIdForAdmin', {
            activityId: matchedActivity.id
        })
            .then(result => {
                setActiviterTickets(result.tickets || []);
                setInitialLoadDone(true);
            })
            .catch(() => {
                setActiviterTickets([]);
            })
            .finally(() => {
                setLoadingTickets(false);
            });
    }, [matchedActivity, initialLoadDone]);

    useEffect(() => {
        loadTickets();
    }, [loadTickets]);

    // Build ticket match map: customerPrototypePk -> activiterTicket
    const ticketMatchMap = {};
    activiterTickets.forEach(ticket => {
        if (ticket.vendor_sku) {
            ticketMatchMap[String(ticket.vendor_sku)] = ticket;
        }
    });

    // Merge FH customer type rates with pricing data
    // getIntegrationInfo returns { availability: { customerTypeRates, customFields }, ticketPrices }
    // ticketPrices shape: [{ customerTypeRatePk, customerPrototypePk, name, priceCents }]
    const fhTickets = [];
    if (details && details.success && details.availability) {
        const priceMap = {};
        (details.ticketPrices || []).forEach(tp => {
            priceMap[tp.customerTypeRatePk] = tp;
        });

        (details.availability.customerTypeRates || []).forEach(rate => {
            const priceInfo = priceMap[rate.pk] || {};
            const cents = priceInfo.priceCents != null
                ? priceInfo.priceCents
                : 0;
            fhTickets.push({
                customerTypeRatePk: rate.pk,
                customerPrototypePk: rate.customerPrototypePk || (rate.customerPrototype ? rate.customerPrototype.pk : null),
                name: rate.name || (rate.customerPrototype ? rate.customerPrototype.display_name : 'Unknown'),
                priceCents: cents,
                priceDisplay: cents ? `$${(cents / 100).toFixed(2)}` : 'N/A'
            });
        });
    }

    const handleUpdatePrice = useCallback(
        async (activiterTicket, newPriceCents) => {
            const key = `price-${activiterTicket.id}`;
            setImporting(prev => ({ ...prev, [key]: true }));
            try {
                await doSwaggerCall('tickets', 'updateTicket', {
                    ticketId: activiterTicket.id,
                    newTicket: {
                        vendor_sku: activiterTicket.vendor_sku,
                        activity_id: activiterTicket.activity_id,
                        name: activiterTicket.name,
                        retail_price: newPriceCents / 100,
                        price_rules: activiterTicket.price_rules || [],
                        booking_fields: activiterTicket.booking_fields || [],
                        addonTickets: (activiterTicket.addonTickets || []).map(t => t.id || t),
                        upgradeToTickets: (activiterTicket.upgradeToTickets || []).map(
                            t => t.id || t
                        ),
                        price_items: activiterTicket.price_items || [],
                        ticket_order: activiterTicket.ticket_order,
                        agent_only: activiterTicket.agent_only || 0
                    }
                });
                loadTickets();
            } catch (err) {
                alert(`Failed to update price: ${err.message}`);
            }
            setImporting(prev => ({ ...prev, [key]: false }));
        },
        [loadTickets]
    );

    /**
     * Setup Ticket: create ticket + run test booking + auto-add missing fields
     */
    const handleSetupTicket = useCallback(
        async (fhTicket, idx) => {
            if (!matchedActivity) return;
            const key = `setup-${fhTicket.customerPrototypePk}`;
            setImporting(prev => ({ ...prev, [key]: true }));
            try {
                // 1. Create the ticket
                const createResult = await doSwaggerCall('tickets', 'createTicket', {
                    newTicket: {
                        vendor_sku: String(fhTicket.customerPrototypePk),
                        activity_id: matchedActivity.id,
                        name: fhTicket.name,
                        retail_price: fhTicket.priceCents / 100,
                        price_rules: [],
                        booking_fields: [],
                        addonTickets: [],
                        upgradeToTickets: [],
                        price_items: [],
                        ticket_order: idx,
                        agent_only: 0
                    }
                });

                // createTicket answers { ticket }
                const ticketId = createResult.ticket.id;

                // 2. Run test booking to discover required fields
                const testResult = await doSwaggerCall('activities', 'testBookingFields', {
                    body: {
                        service: 'FareharborDirect',
                        companyId,
                        itemId: fhItem.id,
                        customerPrototypePk: fhTicket.customerPrototypePk,
                        existingCustomFields: {}
                    }
                });

                // 3. If missing fields, convert and add them
                if (testResult.missingFields && testResult.missingFields.length > 0) {
                    const newFields = convertMissingFieldsToBookingFields(
                        testResult.missingFields,
                        new Set()
                    );
                    if (newFields.length > 0) {
                        await doSwaggerCall('tickets', 'updateTicket', {
                            ticketId,
                            newTicket: {
                                vendor_sku: String(fhTicket.customerPrototypePk),
                                activity_id: matchedActivity.id,
                                name: fhTicket.name,
                                retail_price: fhTicket.priceCents / 100,
                                price_rules: [],
                                booking_fields: newFields,
                                addonTickets: [],
                                upgradeToTickets: [],
                                price_items: [],
                                ticket_order: idx,
                                agent_only: 0
                            }
                        });
                    }
                }

                // 4. Reload
                loadTickets();
            } catch (err) {
                alert(`Failed to setup ticket: ${err.message}`);
            }
            setImporting(prev => ({ ...prev, [key]: false }));
        },
        [matchedActivity, companyId, fhItem, loadTickets]
    );

    /**
     * Run Test Booking on existing matched ticket — discover and add any missing fields
     */
    const handleRunTestBooking = useCallback(
        async (matchedTicket, fhTicket) => {
            const key = `testbooking-${matchedTicket.id}`;
            setImporting(prev => ({ ...prev, [key]: true }));
            try {
                const existingCustomFields = buildExistingCustomFields(
                    matchedTicket.booking_fields
                );

                const testResult = await doSwaggerCall('activities', 'testBookingFields', {
                    body: {
                        service: 'FareharborDirect',
                        companyId,
                        itemId: fhItem.id,
                        customerPrototypePk: fhTicket.customerPrototypePk,
                        existingCustomFields
                    }
                });

                // The agent fills missing fields itself, so a passed booking can
                // still return fields the ticket needs - save them either way.
                const missing = testResult.missingFields || [];
                const existingFieldPks = new Set(
                    (matchedTicket.booking_fields || [])
                        .filter(f => f.fhFieldPk)
                        .map(f => f.fhFieldPk)
                );
                const newFields = convertMissingFieldsToBookingFields(missing, existingFieldPks);
                if (newFields.length > 0) {
                    await doSwaggerCall('tickets', 'updateTicket', {
                        ticketId: matchedTicket.id,
                        newTicket: {
                            vendor_sku: matchedTicket.vendor_sku,
                            activity_id: matchedTicket.activity_id,
                            name: matchedTicket.name,
                            retail_price: parseFloat(matchedTicket.retail_price),
                            price_rules: matchedTicket.price_rules || [],
                            booking_fields: [...(matchedTicket.booking_fields || []), ...newFields],
                            addonTickets: (matchedTicket.addonTickets || []).map(t => t.id || t),
                            upgradeToTickets: (matchedTicket.upgradeToTickets || []).map(
                                t => t.id || t
                            ),
                            price_items: matchedTicket.price_items || [],
                            ticket_order: matchedTicket.ticket_order,
                            agent_only: matchedTicket.agent_only || 0
                        }
                    });
                    loadTickets();
                }

                const added = newFields.length
                    ? ` Added ${newFields.length} required field(s).`
                    : '';
                if (testResult.success) {
                    alert(`Test booking succeeded and was cancelled.${added}`);
                } else if (added || missing.length === 0) {
                    alert(`${testResult.error || 'Test booking failed.'}${added}`);
                } else {
                    alert(
                        `Found ${missing.length} missing field(s) but none could be auto-converted.`
                    );
                }
            } catch (err) {
                alert(`Test booking failed: ${err.message}`);
            }
            setImporting(prev => ({ ...prev, [key]: false }));
        },
        [companyId, fhItem, loadTickets]
    );

    if (!details) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                    Loading item details...
                </Typography>
            </Box>
        );
    }

    if (!details.success) {
        return <Alert severity="warning">{details.error || 'Failed to load item details'}</Alert>;
    }

    if (loadingTickets) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                    Loading activity tickets...
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {fhItem.description && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {fhItem.description.length > 300
                        ? `${fhItem.description.slice(0, 300)}...`
                        : fhItem.description}
                </Typography>
            )}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>FH Ticket</TableCell>
                        <TableCell>FH Price</TableCell>
                        <TableCell align="center">Match</TableCell>
                        <TableCell>Activiter Ticket</TableCell>
                        <TableCell>Activiter Price</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {fhTickets.map((ft, idx) => {
                        const protoPk = String(ft.customerPrototypePk);
                        const matchedTicket = ticketMatchMap[protoPk];
                        const fhPrice = ft.priceCents / 100;
                        const activiterPrice = matchedTicket
                            ? parseFloat(matchedTicket.retail_price)
                            : null;
                        const pricesMatch =
                            matchedTicket && Math.abs(fhPrice - activiterPrice) < 0.01;
                        const priceKey = matchedTicket ? `price-${matchedTicket.id}` : null;
                        const setupKey = `setup-${ft.customerPrototypePk}`;
                        const testBookingKey = matchedTicket
                            ? `testbooking-${matchedTicket.id}`
                            : null;

                        return (
                            <TableRow key={ft.customerTypeRatePk || idx}>
                                <TableCell>
                                    <Typography variant="body2">{ft.name}</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Proto: {ft.customerPrototypePk}
                                    </Typography>
                                </TableCell>
                                <TableCell>${fhPrice.toFixed(2)}</TableCell>
                                <TableCell align="center">
                                    {matchedTicket ? (
                                        <Typography color="primary">&#10003;</Typography>
                                    ) : (
                                        <Typography color="error">&#10007;</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {matchedTicket ? (
                                        <Typography variant="body2">
                                            {matchedTicket.name}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            &mdash;
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {matchedTicket ? (
                                        `$${activiterPrice.toFixed(2)}`
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            &mdash;
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {matchedTicket ? (
                                        pricesMatch ? (
                                            <Chip
                                                label="Prices match"
                                                size="small"
                                                style={{
                                                    backgroundColor: '#e8f5e9',
                                                    color: '#2e7d32'
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                label="Price mismatch"
                                                size="small"
                                                style={{
                                                    backgroundColor: '#fff3e0',
                                                    color: '#e65100'
                                                }}
                                            />
                                        )
                                    ) : (
                                        <Chip label="Not imported" size="small" color="default" />
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {!matchedTicket && matchedActivity && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="primary"
                                            disabled={importing[setupKey]}
                                            onClick={() => handleSetupTicket(ft, idx)}
                                        >
                                            {importing[setupKey]
                                                ? 'Setting up...'
                                                : 'Setup Ticket'}
                                        </Button>
                                    )}
                                    {matchedTicket && (
                                        <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ gap: 4 }}>
                                            {!pricesMatch && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="secondary"
                                                    disabled={importing[priceKey]}
                                                    onClick={() =>
                                                        handleUpdatePrice(
                                                            matchedTicket,
                                                            ft.priceCents
                                                        )
                                                    }
                                                >
                                                    {importing[priceKey]
                                                        ? 'Updating...'
                                                        : `Update to $${fhPrice.toFixed(2)}`}
                                                </Button>
                                            )}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={importing[testBookingKey]}
                                                onClick={() =>
                                                    handleRunTestBooking(matchedTicket, ft)
                                                }
                                            >
                                                {importing[testBookingKey]
                                                    ? 'Testing...'
                                                    : 'Run Test Booking'}
                                            </Button>
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {fhTickets.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={7} align="center">
                                <Typography variant="body2" color="textSecondary">
                                    No ticket types available for this item
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Box>
    );
};

export default FhItemDetailsPanel;
